import { type ComponentPropsWithRef, type ComponentPropsWithoutRef } from 'react';

import { cn } from '@/lib/utils';
import { DynamicImage } from '@/components/dynamic-image';
import type { DynamicImageDimensions } from '@/lib/images/dynamic-image';
import { Button, Icon as IconUI, Typography, type ButtonProps } from '@/shared-ui/atoms';
import { HurryIcon, MinusIcon, PlusIcon } from '@/shared-ui/icons';
import { Price as PriceDisplay } from '@/shared-ui/molecules/price';

type RootProps = ComponentPropsWithRef<'section'> & {
    variant?: 'default' | 'unavailable';
};

const Root = ({ className, ref, variant = 'default', ...props }: RootProps) => (
    <section
        data-slot="bag-card"
        data-variant={variant}
        ref={ref}
        className={cn(
            'relative overflow-visible px-4 pb-4 pt-4 bg-snk-white',
            '[[data-slot=bag-card-list][data-variant=perfect-pairing]_&]:bg-snk-cream', // Update background color when the bag card is used within a perfect pairing list
            className
        )}
        {...props}
    />
);
Root.displayName = 'BagCard.Root';

type ContentProps = ComponentPropsWithRef<'div'>;

const Content = ({ className, ref, ...props }: ContentProps) => (
    <div data-slot="bag-card-content" ref={ref} className={cn('flex gap-4', className)} {...props} />
);
Content.displayName = 'BagCard.Content';

/** No `ref`: the `<img>` sits inside DynamicImage's `<div><picture>`, so there's no single target. */
type ImageProps = Omit<ComponentPropsWithoutRef<'img'>, 'src'> & {
    src: string;
    widths?: DynamicImageDimensions;
    heights?: DynamicImageDimensions;
};

/**
 * Transparent 1×1 pixel rendered while a real image URL isn't available yet (e.g. before basket
 * products are enriched on the first minibag open). With `object-cover` it shows the grey placeholder
 * background instead of the browser's broken-image icon, then the real image replaces it once it loads.
 */
const PLACEHOLDER_PIXEL = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

/** Mirrors the box below — keep the two in sync, or DIS resizes to the wrong dimensions. */
const IMAGE_DIMENSIONS: DynamicImageDimensions = [74, 90];

const IMAGE_BOX_CLASSES =
    'size-[74px] md:size-[90px] shrink-0 [[data-slot=bag-card][data-variant=unavailable]_[data-slot=bag-card-content]_&]:opacity-50';

const Image = ({
    className,
    alt = '',
    src,
    widths = IMAGE_DIMENSIONS,
    heights = IMAGE_DIMENSIONS,
    // Forwarded as DynamicImage props, not via imageProps — it overwrites imageProps' copies.
    loading,
    fetchPriority,
    ...props
}: ImageProps) =>
    src ? (
        <DynamicImage
            // Marks the wrapper, not the <img> — find the image with getByRole('img').
            data-slot="bag-card-image"
            src={src}
            alt={alt}
            widths={widths}
            heights={heights}
            loading={loading}
            priority={fetchPriority}
            className={cn(IMAGE_BOX_CLASSES, className)}
            imageProps={{ decoding: 'async', ...props, className: 'size-full' }}
        />
    ) : (
        // Off the DIS path: a data: URI has no realm, so DynamicImage would only wrap it pointlessly.
        <img
            data-slot="bag-card-image"
            src={PLACEHOLDER_PIXEL}
            alt={alt}
            className={cn(IMAGE_BOX_CLASSES, 'bg-snk-keyline-grey object-cover', className)}
            loading={loading ?? 'lazy'}
            decoding="async"
            fetchPriority={fetchPriority}
            {...props}
        />
    );
Image.displayName = 'BagCard.Image';

type TextWrapperProps = ComponentPropsWithRef<'div'>;

const TextWrapper = ({ className, ref, ...props }: TextWrapperProps) => (
    <div data-slot="bag-card-text-wrapper" ref={ref} className={cn('min-w-0 flex-1', className)} {...props} />
);
TextWrapper.displayName = 'BagCard.TextWrapper';

type ProductHeaderProps = ComponentPropsWithRef<'div'>;

const ProductHeader = ({ className, ref, ...props }: ProductHeaderProps) => (
    <div
        data-slot="bag-card-product-header"
        ref={ref}
        className={cn('flex items-start justify-between gap-4', className)}
        {...props}
    />
);
ProductHeader.displayName = 'BagCard.ProductHeader';

type IconProps = ComponentPropsWithRef<'button'>;

const Icon = ({ className, ref, type = 'button', ...props }: IconProps) => (
    <button
        data-slot="bag-card-action-icon"
        ref={ref}
        type={type}
        className={cn('inline-flex items-center justify-center', className)}
        {...props}
    />
);
Icon.displayName = 'BagCard.Icon';

type ProductTitleProps = ComponentPropsWithRef<'h5'>;

const ProductTitle = ({ className, ref, children, ...props }: ProductTitleProps) => (
    <Typography
        data-slot="bag-card-product-title"
        as="h5"
        variant="h5"
        ref={ref as never}
        className={cn(
            '[[data-slot=bag-card][data-variant=unavailable]_[data-slot=bag-card-content]_&]:text-muted-foreground',
            className
        )}
        weight={'regular'}
        {...props}>
        {children}
    </Typography>
);
ProductTitle.displayName = 'BagCard.ProductTitle';

type BodyTextProps = ComponentPropsWithRef<'p'>;

const Description = ({ className, ref, children, ...props }: BodyTextProps) => (
    <Typography
        data-slot="bag-card-description"
        as="p"
        variant="p"
        weight="light"
        ref={ref as never}
        className={cn(
            'mt-1',
            '[[data-slot=bag-card][data-variant=unavailable]_[data-slot=bag-card-content]_&]:text-muted-foreground',
            className
        )}
        {...props}>
        {children}
    </Typography>
);
Description.displayName = 'BagCard.Description';

const Label = ({ className, ref, children, ...props }: BodyTextProps) => (
    <Typography
        data-slot="bag-card-label"
        as="p"
        variant="p"
        weight="light"
        ref={ref as never}
        className={cn(
            'mt-1 text-[14px]',
            '[[data-slot=bag-card][data-variant=unavailable]_[data-slot=bag-card-content]_&]:text-muted-foreground',
            className
        )}
        {...props}>
        {children}
    </Typography>
);
Label.displayName = 'BagCard.Label';

const HurryMessage = ({ className, ref, children, ...props }: BodyTextProps) => (
    <div className="flex items-center gap-1 mt-2">
        <IconUI icon={HurryIcon} />
        <Typography
            data-slot="bag-card-message"
            as="p"
            variant="p"
            ref={ref as never}
            className={cn('text-[14px] text-snk-deep-pink font-normal', className)}
            {...props}>
            {children}
        </Typography>
    </div>
);
HurryMessage.displayName = 'BagCard.HurryMessage';

type AttributesRowProps = ComponentPropsWithRef<'div'>;

const AttributesRow = ({ className, ref, ...props }: AttributesRowProps) => (
    <div
        data-slot="bag-card-attributes-row"
        ref={ref}
        className={cn('mt-2 flex items-center gap-6 justify-end has-[>*+*]:justify-between', className)} // if price is used as the only attribute, align it to the right, if there are multiple attributes, space them out
        {...props}
    />
);
AttributesRow.displayName = 'BagCard.AttributesRow';

type QuantitySelectorProps = ComponentPropsWithRef<'div'>;

const QuantitySelector = ({ className, ref, ...props }: QuantitySelectorProps) => (
    <div
        data-slot="bag-card-quantity-selector"
        ref={ref}
        className={cn('flex justify-between items-center gap-4', className)}
        {...props}
    />
);
QuantitySelector.displayName = 'BagCard.QuantitySelector';

type QuantityControlProps = Omit<ComponentPropsWithRef<'div'>, 'children'> & {
    quantity: number;
    onDecrease: () => void;
    onIncrease: () => void;
    decreaseAriaLabel?: string;
    increaseAriaLabel?: string;
    buttonClassName?: string;
    iconClassName?: string;
    quantityClassName?: string;
    /** Disables the increase button (e.g. when the line is at its stock maximum). */
    increaseDisabled?: boolean;
};

const QuantityControl = ({
    className,
    ref,
    quantity,
    onDecrease,
    onIncrease,
    decreaseAriaLabel = 'Decrease quantity',
    increaseAriaLabel = 'Increase quantity',
    buttonClassName,
    iconClassName,
    quantityClassName,
    increaseDisabled = false,
    ...props
}: QuantityControlProps) => (
    <QuantitySelector ref={ref} className={className} {...props}>
        <button
            data-slot="bag-card-quantity-decrease"
            type="button"
            aria-label={decreaseAriaLabel}
            onClick={onDecrease}
            className={cn(
                'inline-flex size-8 items-center justify-center rounded-[8px] border border-snk-black',
                buttonClassName
            )}>
            <IconUI icon={MinusIcon} className={cn('size-3', iconClassName)} />
        </button>
        <Typography as="span" variant="h5" className={quantityClassName} weight={'regular'}>
            {quantity}
        </Typography>
        <button
            data-slot="bag-card-quantity-increase"
            type="button"
            aria-label={increaseAriaLabel}
            onClick={onIncrease}
            disabled={increaseDisabled}
            className={cn(
                'inline-flex size-8 items-center justify-center rounded-[8px] border border-snk-black',
                // disabled (e.g. at stock max) reads like the unavailable-variant increase button.
                'disabled:pointer-events-none disabled:border-muted-foreground disabled:text-muted-foreground',
                '[[data-slot=bag-card][data-variant=unavailable]_[data-slot=bag-card-content]_&]:border-muted-foreground',
                '[[data-slot=bag-card][data-variant=unavailable]_[data-slot=bag-card-content]_&]:text-muted-foreground',
                '[[data-slot=bag-card][data-variant=unavailable]_[data-slot=bag-card-content]_&]:pointer-events-none',
                buttonClassName
            )}>
            <IconUI
                icon={PlusIcon}
                className={cn(
                    'size-3',
                    '[[data-slot=bag-card][data-variant=unavailable]_[data-slot=bag-card-content]_&]:text-muted-foreground',
                    iconClassName
                )}
            />
        </button>
    </QuantitySelector>
);
QuantityControl.displayName = 'BagCard.QuantityControl';

type CtaProps = Omit<ButtonProps, 'variant' | 'price' | 'originalPrice'> & {};

const Cta = ({ className, children, ref, type = 'button', ...props }: CtaProps) => (
    <Button data-slot="bag-card-cta" ref={ref} type={type} className={className} {...props}>
        {children}
    </Button>
);
Cta.displayName = 'BagCard.Cta';

type BasePriceProps = Omit<ComponentPropsWithRef<'span'>, 'children' | 'className'> & {
    wrapperClassName?: string;
    oldPriceClassName?: string;
    newPriceClassName?: string;
    priceClassName?: string;
};

type PriceProps =
    | (BasePriceProps & {
          isSalesPrice: true;
          oldPrice: string;
          children: string;
      })
    | (BasePriceProps & {
          isSalesPrice?: false;
          oldPrice?: never;
          children: string;
      });

const Price = ({
    wrapperClassName,
    oldPriceClassName,
    newPriceClassName,
    priceClassName,
    ref,
    children,
    isSalesPrice = false,
    oldPrice,
    ...props
}: PriceProps) => {
    return (
        <div data-slot="bag-card-price" className={cn('flex justify-end flex-wrap', wrapperClassName)}>
            <PriceDisplay.Root className="items-end" {...props}>
                {isSalesPrice ? (
                    <PriceDisplay.Group>
                        <PriceDisplay.Original className={oldPriceClassName}>{oldPrice}</PriceDisplay.Original>
                        <PriceDisplay.Current
                            appearance="sale"
                            ref={ref}
                            className={cn('text-[length:var(--text-h5)]', newPriceClassName)}>
                            {children}
                        </PriceDisplay.Current>
                    </PriceDisplay.Group>
                ) : (
                    <PriceDisplay.Current ref={ref} className={cn('text-[length:var(--text-h5)]', priceClassName)}>
                        {children}
                    </PriceDisplay.Current>
                )}
            </PriceDisplay.Root>
        </div>
    );
};
Price.displayName = 'BagCard.Price';

const BagCard = {
    Root,
    ProductHeader,
    Content,
    Icon,
    Image,
    TextWrapper,
    ProductTitle,
    Description,
    Label,
    HurryMessage,
    AttributesRow,
    QuantitySelector,
    QuantityControl,
    Cta,
    Price,
} as const;

export { BagCard };
